import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Menu, Search, ShoppingBag, Heart, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { usePlatform } from '@/providers/PlatformProvider';
import { ACCOUNT_LINKS, PRIMARY_LINKS, visibleLinks } from '@/config/navigation';
import type { FeatureKey } from '@/types/platform';

/**
 * Fixed bottom navigation — BUILD-PLAN §13/§26/§27.
 *
 * Requirements this satisfies explicitly:
 *   • safe-area support via env(safe-area-inset-bottom) (the .pb-safe utility)
 *   • it must never cover page content — layouts reserve space with
 *     .pb-bottom-nav, sized from the same --bottom-nav-height variable
 *   • large tap targets: each item is at least 44×44px (§27, WCAG 2.2 AA)
 *
 * Entries whose feature is disabled are removed rather than shown inert, so
 * the bar never contains a dead button (§86).
 *
 * ## Why there is a Menu tab
 *
 * Below `lg` the header's link bar is hidden, and five tabs cannot stand in
 * for seven destinations — Occasions, Personalised Gifts, Luxury Gift Boxes,
 * Bespoke Gifting, About and Contact had no route on a phone at all except by
 * scrolling to the footer. That is not a thin navigation bar, it is an
 * unreachable third of the site. The Menu tab opens the full list, so every
 * destination is two taps away at every width.
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Feature gating this entry; undefined means always shown. */
  feature?: FeatureKey;
}

const ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/shop', label: 'Shop', icon: ShoppingBag, feature: 'store' },
  { to: '/search', label: 'Search', icon: Search, feature: 'store' },
  { to: '/wishlist', label: 'Wishlist', icon: Heart, feature: 'wishlist' },
  { to: '/account', label: 'Account', icon: User, feature: 'customer_accounts' },
];

export function BottomNavigation() {
  const { isVisible } = usePlatform();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = ITEMS.filter((item) => !item.feature || isVisible(item.feature));
  const menuLinks = visibleLinks(PRIMARY_LINKS, isVisible);
  const accountLinks = visibleLinks(ACCOUNT_LINKS, isVisible);

  /*
   | Close on navigation.
   |
   | The sheet's own links change the route without unmounting this component,
   | so without this the menu stays open on top of the page the customer just
   | asked for — which reads as the tap having failed.
   */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        /*
         | Not "Primary" — the header's bar already owns that name, and two
         | landmarks with the same label are indistinguishable to anyone
         | navigating by landmark.
         */
        aria-label="Quick navigation"
        /*
         | `lg:hidden`, matching the header's `lg:block` exactly. These two
         | breakpoints are a pair: when this was `md:hidden` and the header
         | moved to `lg`, tablets got no navigation whatsoever. Change one and
         | you must change the other — and `pb-bottom-nav lg:pb-0` in
         | PublicLayout is the third member of the set.
         */
        className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface lg:hidden"
      >
        <ul className="flex items-stretch justify-around">
          {items.map(({ to, label, icon: Icon }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  [
                    'flex h-16 min-w-11 flex-col items-center justify-center gap-1 text-[0.625rem] tracking-wide transition-colors',
                    isActive ? 'text-accent' : 'text-muted',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="h-5 w-5" strokeWidth={isActive ? 2 : 1.5} aria-hidden="true" />
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}

          <li className="flex-1">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-expanded={menuOpen}
              aria-haspopup="dialog"
              className={[
                'flex h-16 w-full min-w-11 flex-col items-center justify-center gap-1 text-[0.625rem] tracking-wide transition-colors',
                menuOpen ? 'text-accent' : 'text-muted',
              ].join(' ')}
            >
              <Menu className="h-5 w-5" strokeWidth={menuOpen ? 2 : 1.5} aria-hidden="true" />
              <span>Menu</span>
            </button>
          </li>
        </ul>
      </nav>

      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} title="Menu">
        <nav aria-label="All sections" className="px-5 py-6">
          <ul className="flex flex-col">
            {menuLinks.map(({ to, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    [
                      // Generous rows rather than a tight list: this is the
                      // only full navigation a phone gets, so it should feel
                      // like a place rather than a dropdown.
                      'flex items-center justify-between border-b border-line py-4 font-display text-lg transition-colors',
                      isActive ? 'text-accent' : 'text-ink hover:text-accent',
                    ].join(' ')
                  }
                >
                  {label}
                  <span aria-hidden="true" className="text-sm text-muted">
                    →
                  </span>
                </NavLink>
              </li>
            ))}
          </ul>

          {accountLinks.length > 0 && (
            <>
              <h3 className="mt-9 text-[0.6875rem] uppercase tracking-[0.25em] text-muted">
                Your account
              </h3>

              <ul className="mt-3 flex flex-col">
                {accountLinks.map(({ to, label }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        [
                          'block border-b border-line py-3.5 text-sm transition-colors',
                          isActive ? 'text-accent' : 'text-muted hover:text-accent',
                        ].join(' ')
                      }
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </>
          )}
        </nav>
      </Drawer>
    </>
  );
}
